import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { Button } from '@connected/ui/components/button';
import { Section } from '@connected/ui/features/layouts';
import { booleanDefaultValue } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Plus } from 'lucide-react';

import { useRisoMeasurements } from '@/hooks/queries/use-riso-measurements';

import type { RisoMeasurementFormProps, RisoMeasurementSubFormValues } from '../utils/riso-measurement-types';

import { useCreateRisoMeasurement } from '../hooks/use-riso-measurement-mutations';
import { RisoMeasurementRow } from './riso-measurement-row';

export function RisoMeasurementForm({
    electricalProjectVersionId,
    distributionBoardDeviceId,
}: RisoMeasurementFormProps) {
    const listQuery = useRisoMeasurements({
        electricalProjectVersionId,
        distributionBoardDeviceId,
    });

    const { mutation: createRisoMeasurementMutation } = useCreateRisoMeasurement({
        electricalProjectVersionId,
    });

    const form = useForm<RisoMeasurementSubFormValues, ZodValidator>({
        defaultValues: {
            numberOfMeasurements: listQuery.data?.measurements?.length || 0,
            measurements: [
                ...(listQuery.data?.measurements?.map(measurement => ({
                    ElectricalProjectRisoMeasurementId: measurement.ElectricalProjectRisoMeasurementId || '',
                    order: measurement.Order || 0,
                    circuitKind: measurement.CircuitKind || null,
                    measuredValue: measurement.MeasuredValue || null,
                    minimumValue: measurement.MinimumValue || null,
                    isFaulty: booleanDefaultValue(measurement.IsFaulty),
                })) || []),
            ],
        },
        validatorAdapter: zodValidator(),
    });

    async function handleNewMeasurement() {
        await createRisoMeasurementMutation.mutateAsync({
            body: {
                ElectricalProjectDistributionBoardDeviceId: distributionBoardDeviceId,
                ElectricalProjectRisoMeasurementId: crypto.randomUUID(),
                CircuitKind: null,
                MeasuredValue: null,
                MinimumValue: null,
                IsFaulty: null,
                Order: (listQuery.data?.count || 0) + 1,
            },
        });
    }

    return (
        <Section>
            <Section.Header className="flex-row justify-between items-center">
                <Section.Title>Metingen</Section.Title>
                <Button size="icon" className="bg-primary hover:bg-primary/90" onClick={handleNewMeasurement}>
                    <Plus size={20} />
                </Button>
            </Section.Header>
            <Section.Content>
                {listQuery?.data?.count === 0
                    ? (
                            <div className="flex flex-row justify-center">
                                <p>Geen metingen</p>
                            </div>
                        )
                    : (
                            <div className="flex flex-col gap-1">
                                <div className="grid grid-cols-4 gap-2">
                                    <p className="text-start px-1 font-bold">Meting</p>
                                    <p className="text-start px-1 font-bold">Gemeten waarde (Ω)</p>
                                    <p className="text-start px-1 font-bold">Minimale waarde</p>
                                    <p className="text-start px-1 font-bold">Akkoord</p>
                                </div>

                                {listQuery?.data?.measurements?.map((item, index) => (
                                    <RisoMeasurementRow
                                        key={item.ElectricalProjectRisoMeasurementId}
                                        index={index}
                                        electricalProjectRisoMeasurementId={item.ElectricalProjectRisoMeasurementId!}
                                        electricalProjectVersionId={electricalProjectVersionId}
                                        form={form}
                                    />
                                ))}
                            </div>
                        )}
            </Section.Content>
        </Section>
    );
}
